import React, { useRef, useEffect, useState } from 'react';
import { GameState, SearchHistoryItem } from '../../domain/entities';
import { View } from '../../App';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { GlobalSearch } from '../shared/GlobalSearch';
import { useUI } from '../../contexts/UIContext';
import { useGameActions } from '../../contexts/GameActionsContext';

interface MainLayoutProps {
    children: React.ReactNode;
    mainContentRef: React.RefObject<HTMLElement>;
    gameState: GameState;
    currentView: View;
    historyIndex: number;
    historyLength: number;
    goBack: () => void;
    goForward: () => void;
    navigate: (view: View) => void;
    showDebug: boolean;
    onOpenBidsModal?: () => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
    children, mainContentRef, gameState, currentView, historyIndex, historyLength,
    goBack, goForward, navigate, showDebug, onOpenBidsModal
}) => {
    const { isMobileNavOpen, toggleMobileNav } = useUI();
    const { updateSearchHistory } = useGameActions();
    const headerRef = useRef<HTMLElement>(null);
    const headerLeftRef = useRef<HTMLDivElement>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const unreadNewsCount = gameState.news?.filter(n => !n.isRead).length || 0;
    const unreadBidsCount = (gameState.incomingBids || [])?.filter(b => b.status === 'pending' || b.status === 'negotiating').length || 0;

    const handleNavigateToPlayer = (playerId: number) => {
        navigate({ type: 'playerDetail', title: 'Player Detail', playerId });
    };

    const handleNavigateToTeam = (teamName: string) => {
        navigate({ type: 'team', title: teamName, teamName, activeTab: 'list' });
    };

    const handleNavigateToLeague = (leagueName: string) => {
        navigate({ type: 'league', title: leagueName, leagueName });
    };

    const handleNavigateToPage = (page: string) => {
        const pageNameMap: Record<string, string> = {
            'Dashboard': 'dashboard',
            'Squad': 'squad',
            'Tactics': 'tactics',
            'Fixtures': 'fixtures',
            'Transfers': 'transfers',
            'Finances': 'finances',
            'Youth Academy': 'youthAcademy',
            'Inbox': 'inbox'
        };

        const pageType = pageNameMap[page] || 'dashboard';
        navigate({ type: pageType as View['type'], title: page });
    };

    const handleNavigateToCompetition = (cupName: string) => {
        navigate({ type: 'competition', title: cupName, cupName });
    };

    const handleAddToSearchHistory = (item: SearchHistoryItem) => {
        updateSearchHistory(item);
    };

    // Set header-left height as CSS variable for mobile navigation positioning
    useEffect(() => {
        const updateHeaderHeight = () => {
            if (window.innerWidth <= 768 && headerLeftRef.current) {
                // On mobile, use the first row height (where the hamburger button is)
                const headerLeftSection = headerLeftRef.current;
                const rect = headerLeftSection.getBoundingClientRect();
                const topOffset = rect.bottom;
                document.documentElement.style.setProperty('--header-height', `${topOffset}px`);
            } else if (headerRef.current) {
                // On desktop, use full header height
                const height = headerRef.current.offsetHeight;
                document.documentElement.style.setProperty('--header-height', `${height}px`);
            }
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    return (
        <div className="app-container">
            <Header
                ref={headerRef}
                headerLeftRef={headerLeftRef}
                gameState={gameState}
                title={currentView.title}
                canGoBack={historyIndex > 0}
                canGoForward={historyIndex < historyLength - 1}
                onBack={goBack}
                onForward={goForward}
                onNavigate={navigate}
                onToggleMobileNav={toggleMobileNav}
                unreadNewsCount={unreadNewsCount}
                unreadBidsCount={unreadBidsCount}
                onOpenBidsModal={onOpenBidsModal}
                onOpenSearch={() => setIsSearchOpen(true)}
            />
            <GlobalSearch
                gameState={gameState}
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                onNavigateToPlayer={handleNavigateToPlayer}
                onNavigateToTeam={handleNavigateToTeam}
                onNavigateToLeague={handleNavigateToLeague}
                onNavigateToPage={handleNavigateToPage}
                onNavigateToCompetition={handleNavigateToCompetition}
                onAddToSearchHistory={handleAddToSearchHistory}
            />
            {isMobileNavOpen && <div className="mobile-nav-scrim" onClick={toggleMobileNav}></div>}
            <div className="main-layout">
                <Navigation
                    activeView={currentView.type}
                    onNavigate={navigate}
                    showDebug={showDebug}
                    hasUnreadNews={unreadNewsCount > 0}
                    isMobileNavOpen={isMobileNavOpen}
                    isAiModeVisible={false}
                    gameState={gameState}
                />
                <main className="main-content" ref={mainContentRef}>
                    {children}
                </main>
            </div>
        </div>
    );
};